import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LayersIcon from '@mui/icons-material/Layers';
import { Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { ReactElement } from 'react';
import { EM_TOGGLE_GROUP_SX } from '../../EntityManagement/components/entityManagementControls';
import { PortfolioTreeMode } from './portfolioTree.types';

export interface IPortfolioTreeSetting {
  enabled: boolean;
  mode: PortfolioTreeMode;
}

interface IProps {
  value: IPortfolioTreeSetting;
  onChange: (next: IPortfolioTreeSetting) => void;
  /** There is no tree to show. List stays live regardless — see below. */
  disabled?: boolean;
  /**
   * Roll-up and Individual would build the same rows: nothing in the tree is held through a
   * vehicle, so there is no row for the modes to disagree on. The pair greys and says so.
   */
  modeInert?: boolean;
}

/**
 * A segment's hover text. The group passes value, size and colour to its buttons through
 * context, so a Tooltip can wrap a segment directly and the pill stays in one piece. The
 * button keeps its own short name; the tooltip describes rather than labels it.
 */
function Segment({ title, children }: { title: string; children: ReactElement }) {
  return (
    <Tooltip title={title} placement='top' describeChild>
      {children}
    </Tooltip>
  );
}

// `disabled` never reaches List: a view saved in Tree mode whose tree is now unavailable still
// needs a way back to the rows the reader can see.
export function PortfolioTreeToggle({ value, onChange, disabled, modeInert }: IProps) {
  const modeGreyed = !value.enabled || Boolean(disabled) || Boolean(modeInert);
  const modeGreyedReason = !value.enabled
    ? 'Roll-up and Individual apply to the Tree layout'
    : disabled
      ? 'There is no tree for Roll-up and Individual to apply to'
      : modeInert
        ? 'Every company here is held directly, so Roll-up and Individual read the same'
        : '';

  return (
    <Stack direction='row' gap='0.5rem' alignItems='center'>
      <ToggleButtonGroup
        color='secondary'
        size='small'
        exclusive
        value={value.enabled ? 'tree' : 'list'}
        sx={EM_TOGGLE_GROUP_SX}
        aria-label='Row layout'
        onChange={(_event, next: 'list' | 'tree' | null) =>
          next && onChange({ ...value, enabled: next === 'tree' })
        }
      >
        <Segment title='List: one row per company'>
          <ToggleButton value='list' aria-label='List'>
            <FormatListBulletedIcon fontSize='small' />
          </ToggleButton>
        </Segment>
        <Segment
          title={disabled ? 'Tree: no legal structure is available for these rows' : 'Tree: rows nested by legal structure'}
        >
          <ToggleButton value='tree' aria-label='Tree' disabled={disabled}>
            <AccountTreeOutlinedIcon fontSize='small' />
          </ToggleButton>
        </Segment>
      </ToggleButtonGroup>

      {/* Always on screen so the row keeps its shape; greyed, with nothing selected, until
          there is a tree the modes would read differently on. The span anchors the one
          tooltip a greyed pair gets, since its segments then say nothing of their own. */}
      <Tooltip title={modeGreyedReason} placement='top'>
        <span>
          <ToggleButtonGroup
            color='secondary'
            size='small'
            exclusive
            value={modeGreyed ? null : value.mode}
            sx={EM_TOGGLE_GROUP_SX}
            disabled={modeGreyed}
            aria-label='Parent row values'
            onChange={(_event, next: PortfolioTreeMode | null) => next && onChange({ ...value, mode: next })}
          >
            <Segment title={modeGreyed ? '' : 'Roll-up: each row combines every company beneath it'}>
              <ToggleButton value='rollup' aria-label='Roll-up'>
                <LayersIcon fontSize='small' />
              </ToggleButton>
            </Segment>
            <Segment title={modeGreyed ? '' : 'Individual: each row shows only what it holds directly'}>
              <ToggleButton value='individual' aria-label='Individual'>
                <CropSquareIcon fontSize='small' />
              </ToggleButton>
            </Segment>
          </ToggleButtonGroup>
        </span>
      </Tooltip>
    </Stack>
  );
}
